import React, { useState, useEffect, useRef, useCallback } from "react";
import { DateRangePicker } from "react-date-range";
import { isSameDay } from "date-fns";

function DateMultiSelectDropdown({ title, dateMinMax, updateFilter, type }) {
  const [isOpen, setIsOpen] = useState(false);
  const [date_range, setDate_range] = useState("");
  const dropdownRef = useRef(null);
  const [error, setError] = useState({ start_Date: "", end_Date: "" });
  const [open, setOpen] = useState(0);
  const [checkData, setCheckData] = useState(false);
  const [date, setDate] = useState([
    { startDate: null, endDate: null, key: "custom" },
  ]);
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // const inputchangeSearch = useCallback(debounce(handleSeachChange), []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [customRangeActive, setCustomRangeActive] = useState(false);
  const [disabledDate, setDisabedDate] = useState(false);

  const handleDateRangeChange = (ranges) => {
    setCheckData(true);
    // setKey(ranges[date[0]?.key]?.key)
    const selectedRange = ranges[date[0]?.key];
    if (selectedRange) {
      const { startDate, endDate } = selectedRange;
      // Format dates as YYYY-MM-DD using toISOString and split
      const formattedStart =
        startDate != "Invalid Date"
          ? new Date(startDate).toISOString().split("T")[0]
          : "";
      const formattedEnd =
        startDate != "Invalid Date"
          ? new Date(endDate).toISOString().split("T")[0]
          : "";
      // Combine the dates with a comma
      const formattedDateRange =
        startDate != "Invalid Date" ? `${formattedStart},${formattedEnd}` : "";

      // Update your state
      if (selectedRange?.key == "allTime") {
        setDate_range("");
      } else {
        setDate_range(formattedDateRange);
      }
    }
    if (selectedRange?.key == "allTime") {
      setDate([{ startDate: "", endDate: "", key: "allTime" }]);
    } else {
      setDate([selectedRange]);
    }

    if (selectedRange.key === "custom") {
      const { startDate, endDate } = selectedRange;
      if (startDate == null && endDate == null) {
        setCustomRangeActive(true);
      } else {
        setCustomRangeActive(false);
      }
      if (startDate && endDate) {
        setError({ ...error, start_Date: "", end_Date: "" });
      }
    } else {
      setCustomRangeActive(false);
      setError({ ...error, start_Date: "", end_Date: "" });
    }
  };

  const staticRanges = [
    {
      label: "Today",
      range: () => {
        const startDate = new Date();
        const endDate = new Date();
        return { startDate, endDate, key: "today" };
      },
      isSelected: (range) =>
        isSameDay(range.startDate, new Date()) &&
        isSameDay(range.endDate, new Date()) &&
        range.key == "today",
    },
    {
      label: "Yesterday",
      range: () => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const startDate = new Date(endDate);
        return { startDate, endDate, key: "yesterday" };
      },
      isSelected: (range) => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return (
          isSameDay(range.startDate, yesterday) &&
          isSameDay(range.endDate, yesterday) &&
          range.key == "yesterday"
        );
      },
    },
    {
      label: "Last 7 days",
      range: () => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 6); // Subtract 6 days from endDate
        return { startDate, endDate, key: "last7Days" };
      },
      isSelected: (range) => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const today = new Date(endDate);
        const last7Days = new Date(today.setDate(today.getDate() - 6));
        return (
          isSameDay(range.startDate, last7Days) &&
          isSameDay(range.endDate, endDate) &&
          range.key == "last7Days"
        );
      },
    },
    {
      label: "Last 30 days",
      range: () => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 29); // Subtract 29 days from endDate
        return { startDate, endDate, key: "last30Days" };
      },
      isSelected: (range) => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const today = new Date(endDate);
        const last30Days = new Date(today.setDate(today.getDate() - 29));
        return (
          isSameDay(range.startDate, last30Days) &&
          isSameDay(range.endDate, endDate) &&
          range.key == "last30Days"
        );
      },
    },
    // Done
    {
      label: "Last 12 months",
      range: () => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1); // Subtract 1 year from endDate
        return { startDate, endDate, key: "last12Months" };
      },
      isSelected: (range) => {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        const today = new Date(endDate);
        const last12Months = new Date();
        last12Months.setFullYear(last12Months.getFullYear() - 1);
        return (
          isSameDay(range.startDate, last12Months) &&
          isSameDay(range.endDate, endDate) &&
          range.key == "last12Months"
        );
      },
    },
    {
      label: "All time",
      range: () => {
        const startDate =
          dateMinMax || new Date() ? new Date() : dateMinMax?.lowestDate;
        const endDate = new Date();
        return { startDate, endDate, key: "allTime" };
      },
      isSelected: (range) =>
        isSameDay(range.startDate, dateMinMax?.lowestDate) &&
        isSameDay(range.endDate, new Date()) &&
        range.key === "allTime",
    },

    // Custom Range
    {
      label: "Custom range",
      range: () => ({
        startDate: new Date(),
        endDate: new Date(),
        key: "custom",
      }),
      isSelected: (range) => {
        return (
          range.key === "custom" &&
          (range.startDate !== null || range.endDate !== null)
        );
      },
      className:
        customRangeActive &&
        (date[0]?.startDate !== null || date[0]?.endDate !== null)
          ? "isActiveDate"
          : "",
    },
  ];

  function isDayDisabled(day) {
    if (date[0]?.key !== "custom") {
      setDisabedDate(true);
      return day.getDay() || day.getDay() === 0;
    } else {
      setDisabedDate(false);
    }
  }

  useEffect(() => {
    if (checkData) {
      
      updateFilter(type, date_range);
    }
  }, [date_range]);
  useEffect(() => {
    setDate([{ startDate: new Date(), endDate: new Date(), key: "today" }]);
  }, []);
  return (
    <div className="relative inline-block text-left w-38" ref={dropdownRef}>
      {/* Dropdown button */}
      <button
        onClick={toggleDropdown}
        type="button"
        className="flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span>{title}</span>
        <svg
          className="w-5 h-5 ml-2 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <DateRangePicker
            className="hide-input-range calander_wrapper"
            ranges={date}
            onChange={handleDateRangeChange}
            staticRanges={staticRanges}
            moveRangeOnFirstSelection={true}
            retainEndDateOnFirstSelection={false}
            startDatePlaceholder={"Start date"}
            showMonthAndYearPickers={false}
            rangeColors={["#D0D5DD"]}
            endDatePlaceholder={"End date"}
            showSelectionPreview={true}
            weekStartsOn={1}
            weekdayDisplayFormat="EEEEEE"
            disabledDay={isDayDisabled}
          />
        </div>
      )}
    </div>
  );
}

export default DateMultiSelectDropdown;
